import { InputAdornment, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { Controller, type ControllerRenderProps } from 'react-hook-form';
import { Decimal } from '@finance/domain-core';
import type { FieldAdapterProps } from '../../../runtime/forms/field-adapter-registry';
import { buildValidationRules } from '../../../runtime/forms/validation-registry';
import { resolveTKey, translateRuntimeTKeyOrFallback } from '../../../i18n/resolve-key';

const DISPLAY_DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/;
const ONE_HUNDRED = Decimal.fromString('100');

function fractionToPercent(value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  try {
    return Decimal.fromString(String(value)).times(ONE_HUNDRED).toString();
  } catch {
    return String(value);
  }
}

function percentToFraction(value: string): unknown {
  const trimmed = value.trim();
  if (trimmed === '') return undefined;
  if (!DISPLAY_DECIMAL_PATTERN.test(trimmed)) return trimmed;
  try {
    return Decimal.fromString(trimmed).div(ONE_HUNDRED).toString();
  } catch {
    return trimmed;
  }
}

function PercentageInput({
  controllerField,
  label,
  helperText,
  error,
  required,
  disabled,
  readonly,
  describedBy,
}: {
  controllerField: ControllerRenderProps;
  label: string;
  helperText?: string;
  error: boolean;
  required: boolean;
  disabled: boolean;
  readonly: boolean;
  describedBy?: string;
}) {
  const focused = useRef(false);
  const [displayValue, setDisplayValue] = useState(() => fractionToPercent(controllerField.value));

  useEffect(() => {
    if (!focused.current) setDisplayValue(fractionToPercent(controllerField.value));
  }, [controllerField.value]);

  return (
    <TextField
      fullWidth
      name={controllerField.name}
      inputRef={controllerField.ref}
      label={label}
      value={displayValue}
      required={required}
      disabled={disabled}
      error={error}
      helperText={helperText}
      aria-invalid={error}
      onFocus={() => {
        focused.current = true;
      }}
      onChange={event => {
        const next = event.target.value;
        setDisplayValue(next);
        controllerField.onChange(percentToFraction(next));
      }}
      onBlur={() => {
        focused.current = false;
        controllerField.onBlur();
        setDisplayValue(fractionToPercent(controllerField.value));
      }}
      slotProps={{
        input: {
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        },
        htmlInput: {
          inputMode: 'decimal',
          pattern: '[+-]?(?:[0-9]+(?:[.][0-9]*)?|[.][0-9]+)',
          autoComplete: 'off',
          spellCheck: false,
          readOnly: readonly,
          'aria-required': required,
          'aria-describedby': describedBy,
        },
      }}
    />
  );
}

/** Human percentage editor with canonical fractional form-state values. */
export function PercentageFieldAdapter(props: FieldAdapterProps) {
  const { field, state, control, formState, serverError, pending, t, formValues, describedBy } = props;
  if (!control) return null;
  const label = t(resolveTKey(field.labelKey));
  const fieldError = formState.errors[field.name]?.message as string | undefined;
  const errorMessage = fieldError
    ? fieldError
    : serverError
      ? translateRuntimeTKeyOrFallback(t, serverError)
      : undefined;
  const helpText = field.helpTextKey ? t(resolveTKey(field.helpTextKey)) : undefined;
  const rules = buildValidationRules(field, t, formValues);

  return (
    <Controller
      control={control}
      name={field.name}
      rules={rules}
      defaultValue={field.value?.defaultValue ?? ''}
      render={({ field: controllerField }) => (
        <PercentageInput
          controllerField={controllerField}
          label={label}
          helperText={errorMessage ?? helpText}
          error={Boolean(errorMessage)}
          required={state.required}
          disabled={state.disabled || pending}
          readonly={state.readonly}
          describedBy={describedBy}
        />
      )}
    />
  );
}
